<template>
  <modal
    :show.sync="internalShow"
    @close="handleClose"
    size="lg"
    :modalClasses="dualFirmMode ? 'modal-payment-dual' : 'modal-xl'"
  >
    <template #header>
      <div>
        <div class="text-xl font-semibold text-slate-900">
          {{ dualFirmMode ? "Service Request Payment" : "Invoice Payment" }}
        </div>
        <div
          v-if="dualFirmMode"
          class="mt-1 text-sm text-slate-500"
        >
          <span class="capitalize">{{ customerName || "—" }}</span>
          <span class="mx-1.5 text-slate-300">·</span>
          <span>SR {{ serviceRequestNumber || "—" }}</span>
          <span class="mx-1.5 text-slate-300">·</span>
          <span
            >{{ selectedInvoices.length }} payable invoice{{
              selectedInvoices.length === 1 ? "" : "s"
            }}</span
          >
        </div>
      </div>
    </template>

    <div class="invoice-payment-popup" :class="{ 'dual-body': dualFirmMode }">
      <!-- Standard customer summary (non-dual) -->
      <div
        v-if="!dualFirmMode"
        class="p-4 bg-gray-100 border-l-4 border-blue-500 rounded-lg customer-summary"
      >
        <h4 class="mb-2 font-bold">Customer Information</h4>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div class="flex flex-col">
            <strong>Customer:</strong>
            <span class="text-blue-600">{{ customerName }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Firm:</strong>
            <span class="text-blue-600">{{ firmName }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Total Invoices:</strong>
            <span class="text-blue-600">{{ selectedInvoices.length }}</span>
          </div>
        </div>
      </div>

      <!-- Dual firm: Rajput | Rajdoot with per-invoice modes -->
      <div v-if="dualFirmMode">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div class="overflow-hidden bg-white rounded-xl border border-red-200 shadow-sm">
            <div
              class="px-3 py-2 text-sm font-semibold tracking-wide text-red-900 uppercase bg-red-100 border-b border-red-200"
            >
              Rajput
              <span class="ml-1 font-normal normal-case text-red-700/80"
                >(sales / chalans)</span
              >
            </div>
            <div class="p-3">
              <div
                v-if="!rajputInvoice"
                class="p-4 text-sm text-center text-slate-500 bg-slate-50 rounded border border-dashed border-slate-200"
              >
                There are no chalans for this request, so there is no Rajput
                bill.
              </div>
              <firm-invoice-panel
                v-else
                :invoice="rajputInvoice"
                :payable-index="payableIndex(rajputInvoice)"
                :discount-amounts="discountAmounts"
                :invoice-payment-amounts="invoicePaymentAmounts"
                :payment-modes="sortedPaymentModes"
                :invoice-mode-amounts="
                  modeAmountsForIndex(payableIndex(rajputInvoice))
                "
                @update-discount="onPanelDiscount"
                @update-payment="onPanelPayment"
                @update-mode="onPanelMode"
                @auto-fill-modes="onAutoFillModes"
              />
            </div>
          </div>

          <div class="overflow-hidden bg-white rounded-xl border border-blue-200 shadow-sm">
            <div
              class="px-3 py-2 text-sm font-semibold tracking-wide text-blue-900 uppercase bg-blue-100 border-b border-blue-200"
            >
              Rajdoot
              <span class="ml-1 font-normal normal-case text-blue-700/80"
                >(labor payment)</span
              >
            </div>
            <div class="p-3">
              <div
                v-if="!rajdootInvoice"
                class="p-4 text-sm text-center text-slate-500 bg-slate-50 rounded border border-dashed border-slate-200"
              >
                Vendor / labor invoice not found for this request.
              </div>
              <firm-invoice-panel
                v-else
                :invoice="rajdootInvoice"
                :payable-index="payableIndex(rajdootInvoice)"
                :discount-amounts="discountAmounts"
                :invoice-payment-amounts="invoicePaymentAmounts"
                :payment-modes="sortedPaymentModes"
                :invoice-mode-amounts="
                  modeAmountsForIndex(payableIndex(rajdootInvoice))
                "
                @update-discount="onPanelDiscount"
                @update-payment="onPanelPayment"
                @update-mode="onPanelMode"
                @auto-fill-modes="onAutoFillModes"
              />
            </div>
          </div>
        </div>

        <div
          v-if="selectedInvoices.length === 0"
          class="p-3 mt-4 text-sm text-green-800 bg-green-50 rounded border border-green-200"
        >
          Both sides are already settled for this service request. No payment is
          due.
        </div>

        <!-- Collapsible payment note -->
        <div v-if="selectedInvoices.length > 0" class="mt-4">
          <button
            type="button"
            class="flex gap-1 items-center text-sm text-blue-600 hover:text-blue-800"
            @click="showPaymentNote = !showPaymentNote"
          >
            <span class="text-xs">{{ showPaymentNote ? "▾" : "▸" }}</span>
            Payment Note (optional)
          </button>
          <textarea
            v-if="showPaymentNote"
            v-model="paymentNote"
            class="px-3 py-2 mt-2 w-full text-sm rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
            rows="2"
            placeholder="Enter any additional payment notes..."
          ></textarea>
        </div>
      </div>

      <!-- Standard Invoice List -->
      <div v-else class="mt-4 invoice-list">
        <h4 class="mb-2 font-bold">Invoice Details</h4>
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase border-b"
                >
                  Invoice No.
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase border-b"
                >
                  Date
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Invoice Amount
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Received
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Balance
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Discount
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Payment Amount
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="(invoice, index) in selectedInvoices"
                :key="index"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-2 text-sm text-gray-900 border-b">
                  {{ invoice.identifier }}
                </td>
                <td class="px-4 py-2 text-sm text-gray-600 border-b">
                  {{ formatDate(invoice.date) }}
                </td>
                <td class="px-4 py-2 text-sm text-right text-gray-900 border-b">
                  {{ invoice.amount | currency }}
                </td>
                <td class="px-4 py-2 text-sm text-right text-gray-600 border-b">
                  {{ invoice.received | currency }}
                </td>
                <td
                  class="px-4 py-2 text-sm font-medium text-right border-b"
                  :class="
                    invoice.balance > 0 ? 'text-red-600' : 'text-green-600'
                  "
                >
                  {{ invoice.balance | currency }}
                </td>
                <td class="px-4 py-2 text-sm border-b">
                  <input
                    v-model.number="discountAmounts[index]"
                    type="number"
                    min="0"
                    :max="invoice.balance"
                    class="px-2 py-1 w-full text-right rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="
                      updateDiscount(index, $event.target.value, invoice.balance)
                    "
                    placeholder="0.00"
                  />
                </td>
                <td class="px-4 py-2 text-sm border-b">
                  <input
                    v-model.number="invoicePaymentAmounts[index]"
                    type="number"
                    min="0"
                    :max="getMaxPaymentAmount(index)"
                    class="px-2 py-1 w-full text-right rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="
                      updateInvoicePaymentAmount(index, $event.target.value)
                    "
                    :placeholder="getCalculatedPaymentAmount(index)"
                  />
                  <div class="mt-1 text-xs text-right text-gray-500">
                    Max: {{ getCalculatedPaymentAmount(index) | currency }}
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50">
              <tr>
                <td colspan="5" class="px-4 py-2 text-sm font-bold text-right">
                  Total Discount:
                </td>
                <td
                  class="px-4 py-2 text-sm font-bold text-right text-orange-600"
                >
                  {{ totalDiscount | currency }}
                </td>
                <td></td>
              </tr>
              <tr>
                <td colspan="5" class="px-4 py-2 text-sm font-bold text-right">
                  Total Payment:
                </td>
                <td class="px-4 py-2 text-sm font-bold text-right text-green-600">
                  {{ totalInvoicePayment | currency }}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Shared payment modes (non-dual only) -->
      <div v-if="!dualFirmMode && selectedInvoices.length > 0" class="mt-4 payment-modes">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-bold">Payment Details</h4>
          <div class="flex gap-2">
            <button
              v-if="totalPaid > 0 && selectedInvoices.length > 1"
              @click="autoDistributePayment"
              class="px-3 py-1 text-xs text-white bg-purple-600 rounded hover:bg-purple-700"
              title="Distribute total payment amount proportionally across invoices"
            >
              Auto Distribute Payment
            </button>
            <button
              v-if="balance !== 0 && totalInvoicePayment > 0"
              @click="autoFillPaymentModes"
              class="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              Auto Fill Payment Modes
            </button>
          </div>
        </div>
        <div class="flex flex-row gap-2 justify-between">
          <div
            v-for="paymentMode in sortedPaymentModes"
            :key="paymentMode.paymentModeId"
            class="flex flex-col p-2 justify-between min-w-[300px] bg-white rounded-lg border"
          >
            <div class="w-1/3 font-medium">
              <a @click="handlePaymentModeClick(paymentMode)">{{
                paymentMode.label
              }}</a>
            </div>
            <div class="w-full">
              <input
                :value="paymentAmounts[paymentMode.paymentModeId] || 0"
                @input="
                  updatePaymentAmount(
                    paymentMode.paymentModeId,
                    $event.target.value
                  )
                "
                type="number"
                min="0"
                class="w-full border border-gray-300 form-input"
                :placeholder="`${paymentMode.label} amount`"
              />
            </div>
          </div>
        </div>

        <div class="p-4 mt-2 bg-gray-100 rounded-lg payment-summary">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <strong>Paying: </strong>
              <span class="text-green-600">{{ totalPaid | currency }}</span>
            </div>
            <div>
              <strong>Balance: </strong>
              <span :class="balanceClass">{{ balance | currency }}</span>
            </div>
          </div>
          <div
            v-if="balance > 0"
            class="p-2 mt-2 text-blue-800 bg-blue-100 rounded border border-blue-400"
          >
            <i class="mr-1 fas fa-info-circle"></i>
            <strong>Info:</strong> This is a partial payment. Remaining balance:
            {{ balance | currency }}.
          </div>
          <div
            v-if="balance < 0"
            class="p-2 mt-2 text-red-800 bg-red-100 rounded border border-red-400"
          >
            <i class="mr-1 fas fa-exclamation-triangle"></i>
            <strong>Error:</strong> Payment amount exceeds invoice payment by
            {{ Math.abs(balance) | currency }}.
          </div>
        </div>

        <div class="mt-4 payment-note">
          <label class="block mb-1 font-medium">Payment Note</label>
          <textarea
            v-model="paymentNote"
            class="w-full border border-gray-300 rounded form-textarea"
            rows="3"
            placeholder="Enter any additional payment notes..."
          ></textarea>
        </div>
      </div>
    </div>

    <template #footer>
      <div
        v-if="dualFirmMode"
        class="flex flex-col gap-3 w-full sm:flex-row sm:items-end sm:justify-between"
      >
        <div class="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
          <div>
            <div class="tracking-wide uppercase">Total Bill</div>
            <div class="text-sm font-semibold text-slate-800">
              {{ dualTotalBill | currency }}
            </div>
          </div>
          <div>
            <div class="tracking-wide uppercase">Discount</div>
            <div class="text-sm font-semibold text-slate-800">
              {{ totalDiscount | currency }}
            </div>
          </div>
          <div>
            <div class="tracking-wide uppercase">Remaining After</div>
            <div
              class="text-sm font-semibold"
              :class="
                dualRemainingAfter <= 0 ? 'text-green-600' : 'text-amber-600'
              "
            >
              {{ dualRemainingAfter | currency }}
            </div>
          </div>
        </div>
        <div class="flex flex-wrap gap-3 items-center justify-end">
          <div class="text-right">
            <div class="text-xs tracking-wide text-slate-500 uppercase">
              Total Payment Now
            </div>
            <div class="text-2xl font-bold text-green-600">
              {{ totalPaid | currency }}
            </div>
          </div>
          <button
            type="button"
            @click="handleClose"
            class="px-4 py-2 text-sm bg-white rounded border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            :disabled="!canSaveDual"
            @click="handleSave"
            class="px-4 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
          >
            <span v-if="saving" class="mr-2 fas fa-spinner fa-spin"></span>
            Save Payment
          </button>
        </div>
      </div>
      <div v-else class="flex justify-end space-x-2">
        <button
          @click="handleClose"
          class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="button"
          :disabled="
            saving ||
              balance < 0 ||
              totalPaid === 0 ||
              selectedInvoices.length === 0
          "
          @click="handleSave"
          class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
        >
          <span v-if="saving" class="mr-2 fas fa-spinner fa-spin"></span>
          Save Payment
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import Modal from "@/components/Modal.vue";
import FirmInvoicePanel from "@/components/FirmInvoicePanel.vue";

export default {
  name: "InvoicePaymentPopup",
  components: { Modal, FirmInvoicePanel },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedInvoices: {
      type: Array,
      default: () => []
    },
    customerName: {
      type: String,
      default: ""
    },
    firmName: {
      type: String,
      default: ""
    },
    saving: {
      type: Boolean,
      default: false
    },
    dualFirmMode: {
      type: Boolean,
      default: false
    },
    serviceRequestNumber: {
      type: String,
      default: ""
    },
    rajputInvoice: {
      type: Object,
      default: null
    },
    rajdootInvoice: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      paymentAmounts: {},
      invoicePaymentAmounts: [],
      discountAmounts: [],
      // Dual mode: one modes map per payable invoice index
      invoiceModeAmounts: [],
      paymentNote: "",
      showPaymentNote: false,
      internalShow: this.show
    };
  },
  computed: {
    ...mapGetters({
      paymentModeList: "paymentMode/getPaymentModes"
    }),
    totalDiscount() {
      return this.discountAmounts.reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
    },
    totalInvoicePayment() {
      return this.selectedInvoices.reduce((sum, invoice, index) => {
        const enteredAmount = parseFloat(this.invoicePaymentAmounts[index]) || 0;
        if (enteredAmount > 0) {
          return sum + enteredAmount;
        }
        const balance = parseFloat(invoice.balance) || 0;
        const discount = parseFloat(this.discountAmounts[index]) || 0;
        return sum + Math.max(0, balance - discount);
      }, 0);
    },
    totalPaid() {
      if (this.dualFirmMode) {
        return this.invoiceModeAmounts.reduce((sum, modes) => {
          return (
            sum +
            Object.values(modes || {}).reduce(
              (s, amount) => s + (parseFloat(amount) || 0),
              0
            )
          );
        }, 0);
      }
      return Object.values(this.paymentAmounts).reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
    },
    balance() {
      return this.totalInvoicePayment - this.totalPaid;
    },
    balanceClass() {
      if (this.balance === 0) return "text-green-600 font-bold";
      if (this.balance > 0) return "text-orange-600 font-bold";
      return "text-red-600 font-bold";
    },
    dualTotalBill() {
      const invoices = [this.rajputInvoice, this.rajdootInvoice].filter(Boolean);
      return invoices.reduce(
        (sum, inv) => sum + (parseFloat(inv.amount) || 0),
        0
      );
    },
    dualRemainingAfter() {
      const openBalances = [this.rajputInvoice, this.rajdootInvoice]
        .filter(Boolean)
        .reduce((sum, inv) => sum + Math.max(0, parseFloat(inv.balance) || 0), 0);
      return Math.max(
        0,
        openBalances - this.totalDiscount - this.totalInvoicePayment
      );
    },
    allInvoiceModesMatch() {
      if (!this.dualFirmMode) return true;
      return this.selectedInvoices.every((_, index) => {
        const payment = parseFloat(this.invoicePaymentAmounts[index]) || 0;
        const modesTotal = Object.values(
          this.invoiceModeAmounts[index] || {}
        ).reduce((s, a) => s + (parseFloat(a) || 0), 0);
        if (payment <= 0) return modesTotal <= 0;
        return Math.abs(modesTotal - payment) < 0.01;
      });
    },
    canSaveDual() {
      return (
        !this.saving &&
        this.selectedInvoices.length > 0 &&
        this.totalPaid > 0 &&
        this.allInvoiceModesMatch &&
        Math.abs(this.balance) < 0.01
      );
    },
    sortedPaymentModes() {
      const order = ["UPI", "Cash", "Card", "Cheque", "Online"];
      const modes = Array.isArray(this.paymentModeList)
        ? this.paymentModeList
        : [];
      return [...modes].sort((a, b) => {
        const aIndex = order.indexOf(a.label);
        const bIndex = order.indexOf(b.label);
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.label.localeCompare(b.label);
      });
    }
  },
  watch: {
    show(newVal) {
      this.internalShow = newVal;
      if (newVal) {
        this.showPaymentNote = false;
        this.initializePaymentAmounts();
        if (this.selectedInvoices && this.selectedInvoices.length > 0) {
          this.discountAmounts = this.selectedInvoices.map(() => 0);
          this.invoicePaymentAmounts = this.selectedInvoices.map(() => 0);
          this.invoiceModeAmounts = this.selectedInvoices.map(() =>
            this.emptyModesMap()
          );
          this.recalculatePaymentAmounts();
          if (this.dualFirmMode) {
            this.selectedInvoices.forEach((_, index) => {
              this.autoFillModesForIndex(index);
            });
          }
        } else {
          this.discountAmounts = [];
          this.invoicePaymentAmounts = [];
          this.invoiceModeAmounts = [];
        }
      }
    },
    internalShow(newVal) {
      if (!newVal) {
        this.$emit("update:show", false);
      }
    }
  },
  methods: {
    emptyModesMap() {
      const map = {};
      (this.sortedPaymentModes || []).forEach(mode => {
        map[mode.paymentModeId] = 0;
      });
      return map;
    },
    modeAmountsForIndex(index) {
      if (index < 0) return {};
      return this.invoiceModeAmounts[index] || {};
    },
    payableIndex(invoice) {
      if (!invoice) return -1;
      return this.selectedInvoices.findIndex(
        inv =>
          (inv.id && inv.id === invoice.id) ||
          (inv.identifier && inv.identifier === invoice.identifier)
      );
    },
    onPanelDiscount({ index, value, balance }) {
      this.updateDiscount(index, value, balance);
    },
    onPanelPayment({ index, value }) {
      this.updateInvoicePaymentAmount(index, value);
    },
    onPanelMode({ index, modeId, value }) {
      if (index < 0) return;
      if (!this.invoiceModeAmounts[index]) {
        this.$set(this.invoiceModeAmounts, index, this.emptyModesMap());
      }
      this.$set(
        this.invoiceModeAmounts[index],
        modeId,
        parseFloat(value) || 0
      );
    },
    onAutoFillModes({ index }) {
      this.autoFillModesForIndex(index);
    },
    autoFillModesForIndex(index) {
      if (index < 0) return;
      const payment = parseFloat(this.invoicePaymentAmounts[index]) || 0;
      const map = this.emptyModesMap();
      const first = this.sortedPaymentModes[0];
      if (first) {
        map[first.paymentModeId] = payment;
      }
      this.$set(this.invoiceModeAmounts, index, map);
    },
    initializePaymentAmounts() {
      this.paymentAmounts = {};
      this.paymentNote = "";
      const modes = this.paymentModeList || [];
      modes.forEach(mode => {
        this.$set(this.paymentAmounts, mode.paymentModeId, 0);
      });
    },
    updatePaymentAmount(modeId, value) {
      this.$set(this.paymentAmounts, modeId, parseFloat(value) || 0);
    },
    updateDiscount(index, value, balance) {
      if (index < 0) return;
      const discount = parseFloat(value) || 0;
      const maxDiscount = parseFloat(balance) || 0;

      if (discount > maxDiscount) {
        this.$set(this.discountAmounts, index, maxDiscount);
        this.$notify({
          message: `Discount cannot exceed balance of ${this.$options.filters.currency(
            maxDiscount
          )}`,
          type: "warning",
          title: "Warning"
        });
      } else {
        this.$set(this.discountAmounts, index, discount);
      }

      this.recalculatePaymentAmount(index);
    },
    recalculatePaymentAmount(index) {
      if (index < 0) return;
      const invoice = this.selectedInvoices[index];
      if (!invoice) return;
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      const maxPaymentAmount = Math.max(0, balance - discount);

      if (
        !this.invoicePaymentAmounts[index] ||
        this.invoicePaymentAmounts[index] === 0
      ) {
        this.$set(this.invoicePaymentAmounts, index, maxPaymentAmount);
      } else {
        const currentAmount =
          parseFloat(this.invoicePaymentAmounts[index]) || 0;
        if (currentAmount > maxPaymentAmount) {
          this.$set(this.invoicePaymentAmounts, index, maxPaymentAmount);
        }
      }
    },
    updateInvoicePaymentAmount(index, value) {
      if (index < 0) return;
      const invoice = this.selectedInvoices[index];
      if (!invoice) return;
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      const maxAmount = Math.max(0, balance - discount);
      const enteredAmount = parseFloat(value) || 0;

      if (enteredAmount > maxAmount) {
        this.$set(this.invoicePaymentAmounts, index, maxAmount);
        this.$notify({
          message: `Payment amount cannot exceed balance (${this.$options.filters.currency(
            maxAmount
          )})`,
          type: "warning",
          title: "Warning"
        });
      } else {
        this.$set(this.invoicePaymentAmounts, index, enteredAmount);
      }
    },
    recalculatePaymentAmounts() {
      this.selectedInvoices.forEach((invoice, index) => {
        this.recalculatePaymentAmount(index);
      });
    },
    getMaxPaymentAmount(index) {
      const invoice = this.selectedInvoices[index];
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      return Math.max(0, balance - discount);
    },
    getCalculatedPaymentAmount(index) {
      return this.getMaxPaymentAmount(index);
    },
    handleClose() {
      this.internalShow = false;
    },
    handleSave() {
      if (this.dualFirmMode) {
        if (!this.allInvoiceModesMatch) {
          this.$notify({
            message:
              "Each invoice’s payment modes total must match its payment amount",
            type: "warning",
            title: "Validation Error"
          });
          return;
        }
        if (this.totalPaid === 0) {
          this.$notify({
            message: "Please enter payment amount in at least one payment mode",
            type: "warning",
            title: "Validation Error"
          });
          return;
        }
        this.$emit("save", this.getPaymentData());
        return;
      }

      if (this.balance < 0) {
        this.$notify({
          message: "Payment amount cannot exceed total invoice payment",
          type: "danger",
          title: "Error"
        });
        return;
      }

      if (this.totalPaid === 0) {
        this.$notify({
          message: "Please enter payment amount in at least one payment mode",
          type: "warning",
          title: "Validation Error"
        });
        return;
      }

      const totalInvoicePayments = this.invoicePaymentAmounts.reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
      const difference = Math.abs(this.totalPaid - totalInvoicePayments);

      if (difference > 0.01) {
        this.$notify({
          message: `Sum of invoice payments (${this.$options.filters.currency(
            totalInvoicePayments
          )}) must match total payment amount (${this.$options.filters.currency(
            this.totalPaid
          )}).`,
          type: "warning",
          title: "Validation Error"
        });
        return;
      }

      this.$emit("save", this.getPaymentData());
    },
    handlePaymentModeClick(paymentMode) {
      Object.keys(this.paymentAmounts).forEach(modeId => {
        this.$set(this.paymentAmounts, modeId, 0);
      });
      this.$set(
        this.paymentAmounts,
        paymentMode.paymentModeId,
        this.totalInvoicePayment
      );
    },
    autoFillPaymentModes() {
      Object.keys(this.paymentAmounts).forEach(modeId => {
        this.$set(this.paymentAmounts, modeId, 0);
      });
      if (this.sortedPaymentModes.length > 0) {
        this.$set(
          this.paymentAmounts,
          this.sortedPaymentModes[0].paymentModeId,
          this.totalInvoicePayment
        );
      }
    },
    autoDistributePayment() {
      const totalPaidAmount = this.totalPaid;
      if (totalPaidAmount <= 0) {
        this.$notify({
          message: "Please enter payment amount in payment modes first",
          type: "warning",
          title: "Warning"
        });
        return;
      }

      const invoicePayableAmounts = this.selectedInvoices.map(
        (invoice, index) => {
          const balance = parseFloat(invoice.balance) || 0;
          const discount = parseFloat(this.discountAmounts[index]) || 0;
          return Math.max(0, balance - discount);
        }
      );
      const totalPayableAmount = invoicePayableAmounts.reduce(
        (sum, amount) => sum + amount,
        0
      );
      if (totalPayableAmount === 0) return;

      this.selectedInvoices.forEach((invoice, index) => {
        const payableAmount = invoicePayableAmounts[index];
        const proportion = payableAmount / totalPayableAmount;
        const roundedAmount =
          Math.round(totalPaidAmount * proportion * 100) / 100;
        this.$set(
          this.invoicePaymentAmounts,
          index,
          Math.min(payableAmount, roundedAmount)
        );
      });
    },
    buildBreakdownFromModes(modesMap) {
      return Object.keys(modesMap || {})
        .filter(modeId => (parseFloat(modesMap[modeId]) || 0) > 0)
        .map(modeId => {
          const mode = (this.paymentModeList || []).find(
            m => m.paymentModeId === modeId
          );
          return {
            paymentModeId: modeId,
            paymentModeLabel: mode ? mode.label : "",
            amount: parseFloat(modesMap[modeId])
          };
        });
    },
    getPaymentData() {
      let paymentBreakdown = [];

      if (this.dualFirmMode) {
        // Aggregate all per-invoice modes for backward-compatible top-level field
        const aggregated = {};
        this.invoiceModeAmounts.forEach(modes => {
          Object.keys(modes || {}).forEach(modeId => {
            const amount = parseFloat(modes[modeId]) || 0;
            if (amount > 0) {
              aggregated[modeId] = (aggregated[modeId] || 0) + amount;
            }
          });
        });
        paymentBreakdown = this.buildBreakdownFromModes(aggregated);
      } else {
        paymentBreakdown = this.buildBreakdownFromModes(this.paymentAmounts);
      }

      const invoicePayments = this.selectedInvoices.map((invoice, index) => {
        const balance = parseFloat(invoice.balance) || 0;
        const discount = parseFloat(this.discountAmounts[index]) || 0;
        const maxPayableAmount = Math.max(0, balance - discount);
        let actualPaymentAmount =
          parseFloat(this.invoicePaymentAmounts[index]) || 0;
        actualPaymentAmount = Math.min(
          maxPayableAmount,
          Math.round(actualPaymentAmount * 100) / 100
        );

        const perInvoiceBreakdown = this.dualFirmMode
          ? this.buildBreakdownFromModes(this.invoiceModeAmounts[index] || {})
          : paymentBreakdown;

        return {
          invoiceId: invoice.identifier,
          invoiceAmount: parseFloat(invoice.amount) || 0,
          id: invoice.id,
          paymentAmount: actualPaymentAmount,
          discount: discount,
          balance: balance,
          firm: invoice.firm || "",
          serviceRequestNumber: invoice.serviceRequestNumber || null,
          paymentBreakdown: perInvoiceBreakdown
        };
      });

      // Backend requires sale (Rajput) paid before vendor (Rajdoot) when both are in one request
      invoicePayments.sort((a, b) => {
        const aRajput = String(a.firm || "")
          .toLowerCase()
          .includes("rajput")
          ? 0
          : 1;
        const bRajput = String(b.firm || "")
          .toLowerCase()
          .includes("rajput")
          ? 0
          : 1;
        return aRajput - bRajput;
      });

      return {
        totalPaid: this.totalPaid,
        totalInvoicePayment: this.totalInvoicePayment,
        balance: this.balance,
        paymentNote: this.paymentNote,
        paymentBreakdown,
        invoicePayments,
        selectedInvoices: this.selectedInvoices
      };
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      }).format(amount);
    }
  }
};
</script>

<style scoped>
.invoice-payment-popup {
  max-height: 70vh;
  overflow-y: auto;
}
.invoice-payment-popup.dual-body {
  max-height: 68vh;
}
</style>

<style>
.modal-xl {
  max-width: 1200px;
}
.modal-payment-dual {
  max-width: 1100px;
}
</style>
